"use client";

import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

interface ProcessStep {
  step: string;
  title: string;
  description: string;
}

interface ProcessFlowSectionProps {
  steps: ProcessStep[];
  accentColor?: string;
  title?: string;
  description?: string;
}

export default function ProcessFlowSection({
  steps,
  accentColor = "#2C78B9",
  title = "How MX-ERP Connects Your Entire Organization",
  description = "A seamless, end-to-end workflow from lead capture to financial closure — all in one platform.",
}: ProcessFlowSectionProps) {
  return (
    <section className="py-16 lg:py-24 bg-white relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50/30 to-white" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-14">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 mb-4"
          >
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ backgroundColor: accentColor }}
            />
            <span
              className="text-[10px] font-bold tracking-[0.2em] uppercase"
              style={{ color: accentColor }}
            >
              Process Flow
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-2xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4 leading-tight"
          >
            {title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed font-medium"
          >
            {description}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.7,
                delay: idx * 0.1,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="group relative flex flex-col p-6 rounded-[28px] bg-white border border-slate-100 shadow-[0_4px_30px_-10px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] hover:border-slate-200 transition-all duration-300"
            >
              <div className="flex items-start gap-4 mb-4">
                <div
                  className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-lg shadow-lg"
                  style={{
                    backgroundColor: accentColor,
                    boxShadow: `0 8px 20px -6px ${accentColor}50`,
                  }}
                >
                  {item.step}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-[17px] font-extrabold text-slate-900 leading-tight tracking-tight">
                    {item.title}
                  </h3>
                </div>
              </div>

              <p className="text-[13px] font-semibold text-slate-500 leading-relaxed pl-16">
                {item.description}
              </p>

              {idx < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-20">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center bg-white border-2 shadow-sm"
                    style={{ borderColor: `${accentColor}40` }}
                  >
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: accentColor }}
                    />
                  </div>
                </div>
              )}

              <div
                className="absolute inset-0 rounded-[28px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{
                  background: `linear-gradient(135deg, ${accentColor}04, transparent)`,
                }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
